/**
 * Piezas de interfaz compartidas por los flujos de MyBarceló.
 *
 * Salieron de mybarcelo-forgot.js cuando el registro necesitó las mismas dos:
 * las seis casillas del código y la política de contraseña en vivo. Se
 * mantienen aquí sin cambiar su comportamiento; lo único nuevo es que el
 * componente de código admite dos alfabetos, porque el OTP de recuperación es
 * alfanumérico ("S4eDsY") y el de registro es numérico ("148331").
 */
(function () {
  'use strict';

  // Qué se descarta al teclear o pegar, según el alfabeto del código.
  var SOBRANTES = {
    alfanumerico: /[^A-Za-z0-9]/g,
    numerico: /[^0-9]/g
  };

  // Qué se considera un código completo y bien formado.
  var COMPLETO = {
    alfanumerico: /^[A-Za-z0-9]{6}$/,
    numerico: /^[0-9]{6}$/
  };

  /**
   * Seis casillas de un carácter, con avance de foco, retroceso encadenado,
   * flechas y pegado repartido entre las casillas.
   *
   * Se respeta la caja tal cual se teclea, porque no está confirmado si CIAM
   * distingue mayúsculas.
   *
   * `alCambiar` recibe un booleano: true si el cambio lo provocó el usuario
   * (teclear, borrar, pegar) y false si fue programático (limpiar()). La
   * distinción importa porque quien consume esto borra el mensaje de error al
   * teclear, y sin ella un `mostrarError(...)` seguido de `limpiar()` se
   * borraría a sí mismo — que es justo lo que pasa al fallar un código.
   */
  function crearComponenteCodigo(opciones) {
    var cajas     = opciones.cajas;
    var tipo      = opciones.tipo || 'alfanumerico';
    var alCambiar = opciones.alCambiar || function () {};

    var sobrantes = SOBRANTES[tipo] || SOBRANTES.alfanumerico;
    var completo  = COMPLETO[tipo] || COMPLETO.alfanumerico;

    function limpiarCaracter(valor) {
      return (valor || '').replace(sobrantes, '').slice(0, 1);
    }

    function leer() {
      return Array.prototype.map.call(cajas, function (c) { return c.value || ''; }).join('');
    }

    function estaCompleto() {
      return completo.test(leer());
    }

    function limpiar() {
      Array.prototype.forEach.call(cajas, function (c) { c.value = ''; });
      if (cajas.length) cajas[0].focus();
      alCambiar(false);
    }

    function enfocar() {
      if (cajas.length) cajas[0].focus();
    }

    Array.prototype.forEach.call(cajas, function (caja, i) {

      caja.addEventListener('input', function () {
        caja.value = limpiarCaracter(caja.value);

        if (caja.value && i < cajas.length - 1) {
          cajas[i + 1].focus();
        }

        alCambiar(true);
      });

      caja.addEventListener('keydown', function (e) {
        // Retroceso en casilla vacía: se va a la anterior y la borra.
        if (e.key === 'Backspace' && !caja.value && i > 0) {
          e.preventDefault();
          cajas[i - 1].value = '';
          cajas[i - 1].focus();
          alCambiar(true);
          return;
        }
        if (e.key === 'ArrowLeft' && i > 0) {
          e.preventDefault();
          cajas[i - 1].focus();
        }
        if (e.key === 'ArrowRight' && i < cajas.length - 1) {
          e.preventDefault();
          cajas[i + 1].focus();
        }
      });

      // Pegar el código entero lo reparte entre las casillas. Es lo que hace
      // todo el mundo al copiarlo del correo.
      caja.addEventListener('paste', function (e) {
        e.preventDefault();

        var texto   = (e.clipboardData || window.clipboardData).getData('text') || '';
        var chars   = texto.replace(sobrantes, '').split('');
        var puestos = 0;

        Array.prototype.forEach.call(cajas, function (c, j) {
          if (j >= i && chars.length) {
            c.value = chars.shift();
            puestos++;
          }
        });

        cajas[Math.min(i + puestos, cajas.length - 1)].focus();

        alCambiar(true);
      });
    });

    return {
      leer: leer,
      completo: estaCompleto,
      limpiar: limpiar,
      enfocar: enfocar
    };
  }

  /**
   * Las tres reglas del diseño, pintadas en vivo.
   *
   * Son una ayuda visual: la política de verdad la impone CIAM y no la
   * conocemos entera, así que cumplirlas no garantiza que el servidor acepte.
   * Sirven sobre todo para no gastar el código en una petición que va a
   * fallar, porque es de un solo uso.
   */
  function crearPoliticaContrasena(opciones) {
    var input     = opciones.input;
    var lista     = opciones.lista;
    var ojo       = opciones.ojo;
    var alCambiar = opciones.alCambiar || function () {};

    function evaluar(password) {
      var p = password || '';
      var r = {
        length: p.length >= 8,
        number: /\d/.test(p),
        letter: /[a-zA-Z]/.test(p)
      };
      r.ok = r.length && r.number && r.letter;
      return r;
    }

    function pintar(resultado) {
      if (!lista) return;

      ['length', 'number', 'letter'].forEach(function (regla) {
        var li = lista.querySelector('[data-rule="' + regla + '"]');
        if (li) li.classList.toggle('is-met', !!resultado[regla]);
      });
    }

    function ok() {
      return evaluar(input ? input.value : '').ok;
    }

    function reiniciar() {
      if (input) {
        input.value = '';
        input.type = 'password';
      }
      if (ojo) ojo.classList.remove('is-visible');
      pintar(evaluar(''));
    }

    if (input) {
      input.addEventListener('input', function () {
        pintar(evaluar(input.value));
        alCambiar(true);
      });
    }

    if (ojo && input) {
      ojo.addEventListener('click', function () {
        var visible = input.type === 'text';
        input.type = visible ? 'password' : 'text';
        ojo.classList.toggle('is-visible', !visible);
      });
    }

    pintar(evaluar(''));

    return {
      evaluar: evaluar,
      pintar: pintar,
      ok: ok,
      reiniciar: reiniciar
    };
  }

  window.BexMybUi = {
    crearComponenteCodigo: crearComponenteCodigo,
    crearPoliticaContrasena: crearPoliticaContrasena
  };
})();
